import { useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import * as ontology from '@intelhub/shared/ontology'
import { api, buildQueryString } from '@/lib/api'
import { Button } from '@/components/ui/button'
import { DashboardSectionHeader } from '@/components/dashboard/DashboardSectionHeader'
import { LeakSitesSafetyNote } from '@/components/leak-sites/LeakSitesSafetyNote'
import { LeakSitesHighlightsBand } from '@/components/leak-sites/LeakSitesHighlightsBand'
import { LeakSitesPulseStrip } from '@/components/leak-sites/LeakSitesPulseStrip'
import {
  LeakSitesWorkbench,
  type WorkbenchState,
} from '@/components/leak-sites/LeakSitesWorkbench'
import { LeakSiteDossierList } from '@/components/leak-sites/LeakSiteDossierList'
import { LeakSitesContextRail } from '@/components/leak-sites/LeakSitesContextRail'
import {
  applyChips,
  deriveGroupConcentration,
  deriveTopMovers,
  enrichDossierClaim,
  formatUtcTimestamp,
  searchClaims,
  sortClaims,
  timeWindowToRange,
} from '@/components/leak-sites/leak-site-utils'
import type {
  ClaimsResponse,
  DashboardSummaryDto,
  GroupListItem,
  GroupsResponse,
  StatsResponse,
} from '@/components/leak-sites/types'

/**
 * Leak Sites — ransomware OSINT (KAN-224 redesign).
 *
 * Public ransomware leak claims presented as professional defensive
 * intelligence: named victims, sectors, sources, and related CVEs in dense
 * dossier rows, framed by a 7-day brief, an operational pulse, and a
 * group-activity rail.
 *
 * This page is the data orchestrator — it owns workbench state, fires the
 * real `/api/leak-sites/*` queries, derives view-models through the pure
 * helpers in `leak-site-utils`, and feeds presentational components. The
 * brief + pulse describe the global landscape (unfiltered); the workbench
 * window / sector / region / group filter the feed server-side, while search /
 * sort / chips refine the loaded page client-side.
 *
 * Every figure is real; unbacked design metrics (mirror health, w/w group
 * deltas, confidence, onion URLs) are omitted rather than invented. The
 * source URL is rendered via a guarded reveal/copy control, never a link.
 *
 * Normal page scroll inside `RootLayout`'s `<main>` — no full-viewport clamp.
 */

const SECTOR_OPTIONS = ontology.industries.map((e) => ({
  slug: e.slug,
  name: e.name,
}))

// Prior 7-day window (8–14 days ago) used for the brief's week-over-week delta.
const WEEK_HOURS = 168

const DEFAULT_STATE: WorkbenchState = {
  window: '7d',
  sort: 'newest',
  search: '',
  sector: '',
  country: '',
  group: '',
  chips: {},
}

export function ThreatsLeakSitesPage() {
  const [searchParams] = useSearchParams()
  const [state, setState] = useState<WorkbenchState>(() => {
    const requestedSector = searchParams.get('sector') ?? ''
    const sector = SECTOR_OPTIONS.some(({ slug }) => slug === requestedSector)
      ? requestedSector
      : ''
    return { ...DEFAULT_STATE, sector }
  })
  const [page, setPage] = useState(1)
  const [expandedId, setExpandedId] = useState<string | null>(null)

  const updateWorkbench = (next: WorkbenchState) => {
    const serverAxisChanged =
      next.window !== state.window ||
      next.sector !== state.sector ||
      next.country !== state.country ||
      next.group !== state.group

    if (serverAxisChanged) {
      setPage(1)
      setExpandedId(null)
    }
    setState(next)
  }

  const claimsQuery = useQuery<ClaimsResponse>({
    queryKey: [
      'leak-sites',
      'claims',
      state.window,
      state.sector,
      state.country,
      state.group,
      page,
    ],
    queryFn: () => {
      const { from, to } = timeWindowToRange(state.window)
      const qs = buildQueryString({
        sector: state.sector,
        country: state.country,
        group: state.group,
        from,
        to,
        page,
      })
      return api.get<ClaimsResponse>(`/leak-sites/claims${qs}`)
    },
  })

  const groupsQuery = useQuery<GroupsResponse>({
    queryKey: ['leak-sites', 'groups', 'all'],
    queryFn: () => api.get<GroupsResponse>('/leak-sites/groups'),
    staleTime: 5 * 60 * 1000,
  })

  const statsQuery = useQuery<StatsResponse>({
    queryKey: ['leak-sites', 'stats', 'global'],
    queryFn: () => api.get<StatsResponse>('/leak-sites/stats'),
    staleTime: 5 * 60 * 1000,
  })

  const priorStatsQuery = useQuery<StatsResponse>({
    queryKey: ['leak-sites', 'stats', 'prior-week'],
    queryFn: () => {
      const now = Date.now()
      const from = new Date(now - 2 * WEEK_HOURS * 3_600_000).toISOString()
      const to = new Date(now - WEEK_HOURS * 3_600_000).toISOString()
      return api.get<StatsResponse>(`/leak-sites/stats${buildQueryString({ from, to })}`)
    },
    staleTime: 5 * 60 * 1000,
  })

  const summaryQuery = useQuery<DashboardSummaryDto>({
    queryKey: ['leak-sites', 'dashboard-summary', '7d'],
    queryFn: () =>
      api.get<DashboardSummaryDto>('/leak-sites/dashboard-summary?days=7'),
    staleTime: 5 * 60 * 1000,
  })

  const groups = useMemo<GroupListItem[]>(
    () => groupsQuery.data?.groups ?? [],
    [groupsQuery.data],
  )
  const groupsById = useMemo(
    () => new Map(groups.map((g) => [g.id, g])),
    [groups],
  )
  const recentByGroup = useMemo(
    () => new Map(groups.map((g) => [g.id, g.recentClaimCount])),
    [groups],
  )
  const topMovers = useMemo(() => deriveTopMovers(groups), [groups])
  const concentration = useMemo(() => deriveGroupConcentration(groups), [groups])

  const rows = useMemo(() => {
    const claims = claimsQuery.data?.claims ?? []
    const enriched = claims.map((c) => enrichDossierClaim(c, groupsById))
    const searched = searchClaims(enriched, state.search)
    const chipped = applyChips(searched, state.chips)
    return sortClaims(chipped, state.sort, { recentByGroup })
  }, [
    claimsQuery.data,
    groupsById,
    recentByGroup,
    state.search,
    state.sort,
    state.chips,
  ])

  const resultCount = claimsQuery.data?.total ?? 0
  const pageSize = claimsQuery.data?.pageSize ?? 20
  const totalPages = Math.max(1, Math.ceil(resultCount / pageSize))

  const hasActiveFilters = Boolean(
    state.sector ||
      state.country ||
      state.group ||
      state.search.trim() ||
      Object.values(state.chips).some(Boolean),
  )

  const retrievedLabel = useMemo(() => {
    const claims = claimsQuery.data?.claims ?? []
    const newest = claims.reduce<string | null>((acc, c) => {
      if (!acc) return c.retrievedAt
      return Date.parse(c.retrievedAt) > Date.parse(acc) ? c.retrievedAt : acc
    }, null)
    return newest ? formatUtcTimestamp(newest) : null
  }, [claimsQuery.data])

  const resetFilters = () => {
    setPage(1)
    setExpandedId(null)
    setState((s) => ({
      ...s,
      search: '',
      sector: '',
      country: '',
      group: '',
      chips: {},
    }))
  }

  const toggleRow = (id: string) =>
    setExpandedId((cur) => (cur === id ? null : id))

  const landscapeLoading = statsQuery.isLoading || summaryQuery.isLoading

  return (
    <div className="space-y-6 p-6">
      <header className="flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-3">
            <h1 className="text-2xl font-semibold tracking-tight">Leak Sites</h1>
            <span className="inline-flex items-center gap-1.5 rounded-full border border-primary/30 bg-primary/10 px-2.5 py-0.5 text-[11px] font-medium text-primary">
              <span className="h-1.5 w-1.5 rounded-full bg-primary" aria-hidden="true" />
              Ransomware OSINT
            </span>
          </div>
          <p className="mt-1 max-w-2xl text-sm text-muted-foreground">
            Public ransomware leak claims — named victims, sectors, sources, and
            related CVEs — surfaced as defensive intelligence and ranked for
            triage.
          </p>
        </div>
        <div className="flex items-center gap-3 text-xs text-muted-foreground">
          <span className="flex items-center gap-1.5">
            <span
              className="h-2 w-2 rounded-full bg-emerald-500 motion-safe:animate-pulse"
              aria-hidden="true"
            />
            Live feed
          </span>
          {retrievedLabel ? (
            <span className="font-mono">Updated {retrievedLabel}</span>
          ) : null}
        </div>
      </header>

      <LeakSitesSafetyNote />

      <section aria-labelledby="leak-sites-brief" className="space-y-3">
        <DashboardSectionHeader
          headingId="leak-sites-brief"
          label="Today's ransomware brief"
          aux="Trailing 7 days"
        />
        <LeakSitesHighlightsBand
          stats={statsQuery.data}
          priorWeekClaims={priorStatsQuery.data?.totals.total ?? null}
          summary={summaryQuery.data}
          isLoading={landscapeLoading || priorStatsQuery.isLoading}
        />
      </section>

      <section aria-labelledby="leak-sites-pulse" className="space-y-3">
        <DashboardSectionHeader
          headingId="leak-sites-pulse"
          label="Operational pulse"
          aux={
            summaryQuery.data?.scopedToUserPrefs
              ? 'Scoped to your watch list'
              : 'Global landscape'
          }
        />
        <LeakSitesPulseStrip
          stats={statsQuery.data}
          summary={summaryQuery.data}
          isLoading={landscapeLoading}
        />
      </section>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[minmax(0,1fr)_320px]">
        <div className="min-w-0 space-y-4">
          <LeakSitesWorkbench
            value={state}
            onChange={updateWorkbench}
            sectorOptions={SECTOR_OPTIONS}
            groupOptions={groups}
            resultCount={resultCount}
            shownCount={rows.length}
          />
          <LeakSiteDossierList
            rows={rows}
            expandedId={expandedId}
            onToggle={toggleRow}
            isLoading={claimsQuery.isLoading}
            isError={claimsQuery.isError}
            hasActiveFilters={hasActiveFilters}
            onResetFilters={resetFilters}
          />
          {!claimsQuery.isLoading && !claimsQuery.isError && rows.length > 0 && totalPages > 1 ? (
            <div className="flex items-center justify-between gap-3 pt-1">
              <Button
                variant="outline"
                size="sm"
                disabled={page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                Previous
              </Button>
              <span className="text-xs tabular-nums text-muted-foreground">
                Page {page} of {totalPages} · {resultCount.toLocaleString('en-GB')} total
              </span>
              <Button
                variant="outline"
                size="sm"
                disabled={page >= totalPages}
                onClick={() => setPage((p) => p + 1)}
              >
                Next
              </Button>
            </div>
          ) : null}
        </div>

        <aside aria-label="Leak site context" className="min-w-0">
          <LeakSitesContextRail
            topMovers={topMovers}
            concentration={concentration}
            summary={summaryQuery.data}
            isLoading={groupsQuery.isLoading}
          />
        </aside>
      </div>
    </div>
  )
}

export default ThreatsLeakSitesPage
